import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, XCircle, Search, FileBadge  } from 'lucide-react';

export default function CertificateVerification({  
  accent = "#115e32",
  pageTitle = "Certificate Verification | Bandarban University",
}) {
  const [form, setForm] = useState({ id_number: '', registration_no: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };
	const handleSubmit = async (e) => {
		e.preventDefault();
		setError(null);
		setResult(null);
		if (!form.id_number) {
			setError('Please enter either ID Number');
			return;
		}
		setLoading(true);
		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				...form, // include name, email, message
			};
			const response = await axios.post(
				`${import.meta.env.VITE_API_BASE_URL}/certificate-verification`,
				payload,
				{
				  headers: { "Content-Type": "application/json" },
				}
			);
			setResult(response.data);
		} catch (err) {
			if (err.response && err.response.data) {
				setError(err.response.data.responseMsg);
			} else {
				setError('Unable to verify certificate. Please try again later.');
			}
			console.log(err);
		} finally {
			setLoading(false);
		}
	};
 
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-700 to-teal-500  text-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden text-center py-20">
        <motion.div initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="relative z-10">
          <div className="flex justify-center mb-6">
            <FileBadge  className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">Certificate Verification</h1>
          <p className="text-white text-lg max-w-2xl mx-auto">
            Verify your academic certificate online by entering either your ID Number or Registration No.
          </p>
        </motion.div>

        {/* SVG Background */}
        <svg className="absolute bottom-0 left-0 w-full h-24 text-yellow-200" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
          <path fill="#6BCCF2" d="M0,160L60,154.7C120,149,240,139,360,138.7C480,139,600,149,720,165.3C840,181,960,203,1080,213.3C1200,224,1320,224,1380,224L1440,224L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"></path>
        </svg>
      </section>

      {/* Verification Form Section */}
      <section className="max-w-4xl mx-auto px-6 py-10">
        <motion.form onSubmit={handleSubmit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="bg-white shadow-lg rounded-2xl p-8 border border-amber-100">
          <h2 className="text-2xl font-bold text-green-700 mb-4 text-center">Enter Your Details</h2>

          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-semibold text-green-900 mb-1">ID Number</label>
              <input
                type="text"
                name="id_number"
                value={form.id_number}
                onChange={handleChange}
                className="w-full border border-amber-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 text-black"
                placeholder="e.g., 0941910013201002"
              />
            </div>
           
          </div>

          <div className="mt-6 flex justify-center">
            <button type="submit" disabled={loading} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#016C94] text-white font-semibold hover:bg-amber-800 transition-all">
              {loading ? 'Verifying...' : (<><Search className="w-5 h-5" /> Verify Now</>)}
            </button>
          </div>

          {error && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-center">
              <XCircle className="inline w-5 h-5 mr-2" /> {error}
            </motion.div>
          )}
        </motion.form>

        {/* Verification Result */}
        {result && result.id_number && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-10 bg-white rounded-2xl shadow-lg border border-amber-100 p-6">
			<div className="flex items-center">
				<img src="assets/img/bubbanlogo.jpg" className="w-[10%] object-center mx-auto"/>
				<h2 className="text-green-900 font-bold text-4xl flex-1">Bandarban University</h2>
			</div>
			<div className="flex items-center gap-3 mb-4">
              <CheckCircle className="text-green-600 w-6 h-6" />
              <h3 className="text-2xl font-bold text-[#015A7A]">Certificate Verified Successfully</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-[#014864]">
              <div>
                <p><strong>ID Number:</strong> {result.id_number}</p>
                <p><strong>Name:</strong> {result.name}</p>
                <p><strong>Programme:</strong> {result.programme_name}</p>
                <p><strong>Semester:</strong> {result.gr_semester}</p>
                <p><strong>CGPA:</strong> {result.cgpa}</p>
				<p><strong>National ID:</strong> {result.national_id}</p>
				<p><strong>Passport No.:</strong> {result.passport_no}</p>
              </div>
              <div>
                <p><strong>Registration No:</strong> {result.registration_no}</p>
                <p><strong>Credits Completed:</strong> {result.complete_credit}/{result.req_credit}</p>
                <p><strong>Meeting Date:</strong> {result.meeting_date}</p>
                <p><strong>Convocation:</strong> No. {result.convocation_no}</p>
                <p><strong>Mobile:</strong> {result.mobile_number}</p>
				<p><strong>Birth Registration No.:</strong> {result.birth_certificate}</p>
				<p><strong>Email:</strong> {result.email}</p>
              </div>
			   {result.picture && (
				  <div className="mt-6 flex justify-center">
					<img src={`${import.meta.env.VITE_API_SOURCE_URL}/${result.picture}`} alt="Student" className="w-40 h-40 object-cover rounded-xl shadow-md border border-amber-100" />
				  </div>
				)}
            </div>
            
          </motion.div>
        )}
      </section>

      <footer className="py-10 text-center text-sm text-[#016C94]">
        For manual verification, please email <a href="mailto:coe@bubban.ac.bd" className="underline text-amber-800">coe@bubban.ac.bd</a> or call <strong> +88-0131-8305900</strong>.
      </footer>
    </div>
  );
}
